import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "./useDebounce";
import { useAuth } from "../auth/AuthContext";
import { buildQuery, parsePagedResponse } from "../lib/paged";

type UsePagedQueryOptions = {
  endpoint: string;
  filters?: Record<string, unknown>;
  orderBy?: string;
  orderDir?: "asc" | "desc";
  initialQ?: string;
  initialTake?: number;
  debounceMs?: number;
  enabled?: boolean;
  pause?: boolean;
};

export const usePagedQuery = <T,>({
  endpoint,
  filters = {},
  orderBy,
  orderDir,
  initialQ = "",
  initialTake = 20,
  debounceMs = 350,
  enabled = true,
  pause = false,
}: UsePagedQueryOptions) => {
  const { client } = useAuth();
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState(initialQ);
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(initialTake);
  const activeRequestRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const debouncedQ = useDebounce(q, debounceMs);
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const stableFilters = useMemo<Record<string, unknown>>(() => {
    try {
      return JSON.parse(filtersKey) as Record<string, unknown>;
    } catch {
      return {};
    }
  }, [filtersKey]);

  const skip = useMemo(() => (page - 1) * take, [page, take]);

  const queryString = useMemo(
    () =>
      buildQuery({
        q: debouncedQ,
        take,
        skip,
        orderBy,
        orderDir,
        ...stableFilters,
      }),
    [debouncedQ, take, skip, orderBy, orderDir, stableFilters]
  );

  const load = useCallback(async () => {
    if (!enabled || pause) {
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;

    setLoading(true);
    setError(null);
    try {
      const response = await client.get(`${endpoint}?${queryString}`, {
        signal: controller.signal,
      });
      if (requestId !== activeRequestRef.current) {
        return;
      }
      if (!response.ok) {
        const body = await response.text().catch(() => "");
        console.error("PagedQuery error", {
          endpoint,
          status: response.status,
          body,
        });
        setRows([]);
        setTotal(0);
        setError("Falha ao carregar dados.");
        return;
      }
      const payload = await response.json();
      const parsed = parsePagedResponse<T>(payload);
      setRows(parsed.data);
      setTotal(parsed.total);
    } catch (err) {
      if (
        err instanceof DOMException &&
        err.name === "AbortError"
      ) {
        return;
      }
      if (requestId !== activeRequestRef.current) {
        return;
      }
      console.error("PagedQuery exception", { endpoint, error: err });
      setRows([]);
      setTotal(0);
      setError("Falha ao carregar dados.");
    } finally {
      if (requestId === activeRequestRef.current) {
        setLoading(false);
      }
    }
  }, [client, endpoint, queryString, enabled, pause]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, filtersKey]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      setError(null);
      return;
    }
    if (pause) {
      setLoading(false);
      return;
    }
    load();
  }, [load, enabled, pause]);

  return {
    rows,
    total,
    loading,
    error,
    page,
    take,
    setPage,
    setTake,
    q,
    setQ,
    reload: load,
  };
};
